import React from 'react';
import { motion } from 'framer-motion';
import { useParallaxRef } from '@/hooks/useParallax';
import { XCircle, CheckCircle } from 'lucide-react';

interface DomainGapSectionProps {
  domainName: string;
  category?: string | null;
}

export default function DomainGapSection({ domainName, category }: DomainGapSectionProps) {
  const subtitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 12 });

  const painPoints = [
    'Hiring random freelancers who don’t understand your niche or clients.',
    'Generic templates that make a premium domain feel like a commodity.',
    'Six months of “figuring it out” instead of signing better clients sooner.',
    'Inconsistent branding and messaging that confuses prospects.',
  ];

  const advantages = [
    `Domain, brand, and site designed as one cohesive system${category ? ` for ${category}` : ''}.`,
    'Conversion-focused copy and layouts tuned for professional buyers.',
    'Clear launch plan so you can start talking to clients in weeks, not months.',
    'Optional ongoing support to refine and scale as you grow.',
  ];

  const containerVariants = {
    hidden: { opacity: 0, y: 16 },
    show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
  };

  const colVariants = {
    hidden: { opacity: 0, y: 14 },
    show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
  };

  return (
    <section className="bg-white py-16 md:py-20">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 space-y-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="text-center space-y-3"
        >
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            The Domain Is Just Land. I Help You Build the Skyscraper.
          </h2>
          <p
            ref={subtitleRef as React.RefObject<HTMLParagraphElement>}
            className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto"
          >
            Most founders waste months piecing branding and a site together. I give you a proven path from {domainName} to booked clients.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <motion.div
            variants={colVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="p-6 rounded-2xl border border-slate-200 bg-white shadow-sm"
          >
            <h3 className="text-lg font-semibold text-foreground mb-2">The DIY Trap</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              Buying a great domain is only the first step. Most founders then get stuck in a maze of freelancers, templates, and decision fatigue—losing months before they have something they’re proud to show clients.
            </p>
            <ul className="space-y-3">
              {painPoints.map((point, idx) => (
                <li key={idx} className="flex items-start gap-3 text-sm text-foreground">
                  <XCircle className="w-4 h-4 text-red-500 mt-0.5" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            variants={colVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            transition={{ delay: 0.08 }}
            className="p-6 rounded-2xl border border-primary/20 bg-primary/5 shadow-sm"
          >
            <h3 className="text-lg font-semibold text-foreground mb-2">The Attain Launch Advantage</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              With Attain Domains, you’re not just buying {domainName}. You’re plugging into a launch system that turns this asset into a full brand, website, and client-generating platform.
            </p>
            <ul className="space-y-3">
              {advantages.map((point, idx) => (
                <li key={idx} className="flex items-start gap-3 text-sm text-foreground">
                  <CheckCircle className="w-4 h-4 text-[#0000FF] mt-0.5" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        <div className="text-center text-sm text-muted-foreground">
          If you’d like help turning {domainName} into a fully-booked brand, my Launch Studio can handle everything from branding to build to launch.{' '}
          <a href="/services" className="text-primary hover:underline">
            Explore Launch Studio services →
          </a>
        </div>
      </div>
    </section>
  );
}

